import React from "react";
// Libraries
import { useQuery } from "@apollo/client";
import { capitaliseFirstLetter } from "@breedr/utils";
// Components
import CheckBox from "components/Common/Checkbox";
import { FieldNumber, FieldSelect, FieldText, FieldTextarea, Flex, Spacer, Title } from "components";
// Utils
import { GET_BUSINESS_UNIT_SIZE_UNIT } from "api/BusinessUnits/queries";
import {
  GetBusinessUnitSizeUnit,
  GetBusinessUnitSizeUnitVariables,
} from "api/BusinessUnits/types/GetBusinessUnitSizeUnit";
import { GET_PASTURE_TYPES } from "api/MyLivestock/Fields/queries";
import { GetPastureTypes } from "api/MyLivestock/Fields/types/GetPastureTypes";
import { GET_DELIVERY_ADDRESSES } from "trading/api/queries";
import { deliveryAddresses, deliveryAddressesVariables } from "trading/api/types/deliveryAddresses";
import { getBUFromLocalStorage } from "helpers/storage";
import { handleEnterPress, handleErrorCheck } from "helpers/general";
import { useBreakpoints } from "hooks";

interface CreateFieldFormProps {
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleChange: () => void;
  values: FieldForm;
  errors: FieldForm;
}

interface SelectorOption {
  label: string;
  value: string;
  key: string;
}

const getTradingLocation = (
  { unitName, addressLine1, addressLine2, city, country, postcode }, // eslint-disable-line
) =>
  (unitName ? `${unitName}: ` : "") +
  (addressLine1 ? `${addressLine1}, ` : "") +
  (addressLine2 ? `${addressLine2}, ` : "") +
  (city ? `${city}, ` : "") +
  (country && country.name ? `${country.name}, ` : "") +
  (postcode ? `${postcode}` : "");

const CreateFieldForm: React.FC<CreateFieldFormProps> = ({ errors, values, handleChange, handleSubmit }) => {
  const { data: businessUnitData } = useQuery<GetBusinessUnitSizeUnit, GetBusinessUnitSizeUnitVariables>(
    GET_BUSINESS_UNIT_SIZE_UNIT,
    {
      variables: {
        businessUnit: getBUFromLocalStorage(),
      },
    },
  );
  const businessUnitSizeUnit = businessUnitData?.businessUnit?.settings?.sizeUnit;

  const { data } = useQuery<GetPastureTypes>(GET_PASTURE_TYPES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const { data: addressesData } = useQuery<deliveryAddresses, deliveryAddressesVariables>(GET_DELIVERY_ADDRESSES, {
    variables: {
      businessUnit: getBUFromLocalStorage(),
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const types: SelectorOption[] | [] =
    data && data.pastureTypes
      ? data.pastureTypes.map((item) => ({
          label: item?.name || "",
          value: item?.id || "",
          key: item?.id || "",
        }))
      : [];

  const addressesWithLocationId = addressesData?.deliveryAddresses?.filter((item) => item?.locationId) || [];

  const addresses: SelectorOption[] | [] = addressesWithLocationId.map((address) => ({
    label: address ? `${getTradingLocation(address)}/${address.locationId || ""}` : "",
    value: address?.id || "",
    key: address?.id || "",
  }));

  const { isBreakpointM } = useBreakpoints();

  return (
    <>
      <Flex container>
        <Flex item itemGutter>
          <Title secondary>{capitaliseFirstLetter("location")} details</Title>
        </Flex>
      </Flex>

      <Spacer baselineHeight={2} />

      <Flex container containerAlignItems="flex-end">
        <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
          <FieldText
            label={`${capitaliseFirstLetter("location")} Name *`}
            error={handleErrorCheck(errors.name)}
            inputProps={{
              name: "name",
              defaultValue: values.name,
              disabled: values.isHoldingPen,
              onChange: handleChange,
              onKeyPress: (event): void => handleEnterPress(event, handleSubmit),
            }}
          />
        </Flex>
        <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
          <FieldSelect
            label="Location Address *"
            error={handleErrorCheck(errors.location)}
            inputProps={{
              name: "location",
              value: values.location,
              options: addresses,
              onChange: handleChange,
            }}
          />
        </Flex>
        <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
          <CheckBox
            type="text"
            label="Is this a Pen?"
            name="isYard"
            checked={values.isYard}
            value={values.isYard}
            errorStatus={errors.isYard}
            onChange={handleChange}
            onKeyPress={(event): void => handleEnterPress(event, handleSubmit)}
            error={handleErrorCheck(errors.isYard)}
          />
        </Flex>

        {values.isYard ? null : (
          <>
            {" "}
            {isBreakpointM && <Spacer />}
            <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
              <FieldNumber
                label={`${capitaliseFirstLetter("location")} Size`}
                error={handleErrorCheck(errors.size)}
                inputProps={{
                  name: "size",
                  onChange: handleChange,
                  onKeyPress: (event): void => handleEnterPress(event, handleSubmit),
                  value: values.size,
                  adornment: businessUnitSizeUnit
                    ? {
                        labelFor: "size",
                        label: businessUnitSizeUnit,
                      }
                    : undefined,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
              <FieldSelect
                label="Field/Pasture Type"
                error={handleErrorCheck(errors.pastureTypes)}
                inputProps={{
                  name: "pastureTypes",
                  value: values.pastureTypes,
                  options: types,
                  multiple: true,
                  onChange: handleChange,
                }}
              />
            </Flex>
            <Flex item itemGutter xs={12} s={6} m={4} l={3} xl={2}>
              <CheckBox
                type="text"
                label="Is the location organic?"
                name="isOrganic"
                checked={values.isOrganic}
                value={values.isOrganic}
                errorStatus={errors.isOrganic}
                onChange={handleChange}
                onKeyPress={(event): void => handleEnterPress(event, handleSubmit)}
                error={handleErrorCheck(errors.isOrganic)}
              />
            </Flex>
          </>
        )}
      </Flex>
      <Flex container>
        <Flex item itemGutter xs={12} l={9} xl={6}>
          <FieldTextarea
            label="Description"
            error={handleErrorCheck(errors.description)}
            inputProps={{
              name: "description",
              defaultValue: values.description,
              onChange: handleChange,
            }}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default CreateFieldForm;
